import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { List, Map, Set as ImmutableSet } from 'immutable';
import { Extension } from 'flow/types/Extension';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { extractName } from 'flow/utils';
import { getContactName } from 'flow/utils/contact-utils';
import { LIST_HEADER_ITEM_HEIGHT } from 'flow/flow-theme';
import {
    selectExtensions,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import ClickAwayListener from 'flow/components/ClickAwayListener';
import MentionPickerHeading from './MentionPickerHeading';
import MentionPickerItem from './MentionPickerItem';

const LIST_EXTENSIONS_EMPTY = List<TypedMap<Extension>>();

const listPaddingY = 16;
const itemHeight = 48;
const listMaxHeight = 288 + listPaddingY; // 72 * 4 (h-72)

type PickerRow =
    | { kind: 'heading'; labelKey: string }
    | {
          kind: 'mentionee';
          extensionKey: ExtensionKey;
          suggestionIndex: number;
      };

const getRowHeight = (row: PickerRow) =>
    row.kind === 'heading' ? LIST_HEADER_ITEM_HEIGHT : itemHeight;

const withHeading = (labelKey: string, rows: PickerRow[]): PickerRow[] =>
    rows.length > 0 ? [{ kind: 'heading', labelKey }, ...rows] : [];

interface RowData {
    rows: PickerRow[];
    indexSelected: number;
    onClickMentionable: (event: any) => void;
}

// Keyed by who the row shows, not by index. A heading moves the rows below it,
// and a row keyed by index would then remount.
const getRowKey = (index: number, data: RowData) => {
    const row = data.rows[index];
    return row.kind === 'heading' ? row.labelKey : row.extensionKey;
};

// react-window uses this as a component type. An inline arrow is a new type on
// each render, so every row would remount.
const MentionPickerRow = ({
    index,
    style,
    data,
}: ListChildComponentProps<RowData>) => {
    const row = data.rows[index];
    if (row.kind === 'heading') {
        return <MentionPickerHeading style={style} labelKey={row.labelKey} />;
    }

    return (
        <MentionPickerItem
            index={row.suggestionIndex}
            style={style}
            extensionKey={row.extensionKey}
            isActive={data.indexSelected === row.suggestionIndex}
            onClick={data.onClickMentionable}
        />
    );
};

export interface MentionPickerHandle {
    moveSelectionUp: () => void;
    moveSelectionDown: () => void;
    confirmSelection: () => void;
}

interface Props {
    memberKeys: ImmutableSet<ExtensionKey>;
    nonMembersAreMentionable: boolean;
    onCloseRequested: () => void;
    onSelect: (name: string, extensionKey?: ExtensionKey) => void;
    text: string;
}

const MentionPicker = forwardRef<MentionPickerHandle, Props>(
    (
        {
            memberKeys,
            nonMembersAreMentionable,
            onCloseRequested,
            onSelect,
            text,
        },
        ref,
    ) => {
        const listRef = useRef<HTMLDivElement>(null);
        const variableSizeListRef = useRef<VariableSizeList>(null);
        const [indexSelected, setIndexSelected] = useState(0);
        const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
        const extensions = useAppSelector(selectExtensions);

        const memberMentionees: List<TypedMap<Extension>> = useMemo(
            () =>
                memberKeys
                    ?.reduce((accumulator: any, key) => {
                        const extension = extensions.get(key);
                        return extension &&
                            key !== ownExtensionKey &&
                            extensionIsMentionable(extension)
                            ? accumulator.set(key, extension)
                            : accumulator;
                    }, Map())
                    .sortBy(getMentioneeName)
                    .toList() || LIST_EXTENSIONS_EMPTY,
            [extensions, memberKeys, ownExtensionKey],
        );

        const nonMemberMentionees: List<TypedMap<Extension>> = useMemo(() => {
            if (!nonMembersAreMentionable) return LIST_EXTENSIONS_EMPTY;

            return extensions
                .filter(
                    (extension, key) =>
                        key !== ownExtensionKey &&
                        !memberKeys?.has(key) &&
                        extensionIsMentionable(extension),
                )
                .sortBy(getMentioneeName)
                .toList();
        }, [extensions, memberKeys, nonMembersAreMentionable, ownExtensionKey]);

        const mentioneesPossible = useMemo(
            () => memberMentionees.concat(nonMemberMentionees).toList(),
            [memberMentionees, nonMemberMentionees],
        );

        const headingsAreShown =
            memberMentionees.size > 0 && nonMemberMentionees.size > 0;

        const suggestions = useMemo(() => {
            if (text === '') return LIST_EXTENSIONS_EMPTY;

            const textWithoutAtLowerCase = text?.startsWith('@')
                ? text?.substring(1)?.toLowerCase()
                : text?.toLowerCase();
            return mentioneesPossible.filter((mentionee) =>
                getMentioneeMatchesText(mentionee, textWithoutAtLowerCase),
            );
        }, [mentioneesPossible, text]);

        const rows: PickerRow[] = useMemo(() => {
            const mentioneeRows = suggestions
                .map(
                    (mentionee, index): PickerRow => ({
                        kind: 'mentionee',
                        extensionKey: mentionee.get('key'),
                        suggestionIndex: index,
                    }),
                )
                .toArray();
            if (!headingsAreShown) return mentioneeRows;

            // The suggestions keep the members first.
            const memberRowCount = suggestions.count((mentionee) =>
                memberKeys?.has(mentionee.get('key')),
            );
            return [
                ...withHeading(
                    'tickets.ticket.ticket_members',
                    mentioneeRows.slice(0, memberRowCount),
                ),
                ...withHeading(
                    'tickets.ticket.other_colleagues',
                    mentioneeRows.slice(memberRowCount),
                ),
            ];
        }, [headingsAreShown, memberKeys, suggestions]);

        const listStyle = useMemo(() => {
            // Stable reference to this object, so that the div where this is set
            // to the style object does not get a new object if rows haven't changed.
            // How much of a difference it makes I don't know.
            const totalHeight = rows.reduce(
                (sum, row) => sum + getRowHeight(row),
                listPaddingY,
            );

            return { height: Math.min(totalHeight, listMaxHeight) };
        }, [rows]);

        const pickMentionee = useCallback(
            (mentionee?: TypedMap<Extension>) =>
                onSelect(
                    extractName(mentionee?.get('contact')),
                    mentionee?.get('key'),
                ),
            [onSelect],
        );

        const onClickMentionable = useCallback(
            (event: any) =>
                pickMentionee(
                    suggestions.get(
                        event.currentTarget.getAttribute('data-index'),
                    ),
                ),
            [suggestions, pickMentionee],
        );

        const rowData: RowData = useMemo(
            () => ({ rows, indexSelected, onClickMentionable }),
            [rows, indexSelected, onClickMentionable],
        );

        useEffect(() => {
            const activeItem = listRef.current?.querySelector('.active');
            activeItem?.scrollIntoView({ block: 'nearest' });
        }, [indexSelected]);

        const confirmSelection = useCallback(
            () => pickMentionee(suggestions.get(indexSelected)),
            [suggestions, indexSelected, pickMentionee],
        );

        const moveSelectionUp = useCallback(
            () =>
                setIndexSelected((prevIndex) =>
                    prevIndex === 0 ? 0 : prevIndex - 1,
                ),
            [],
        );

        const moveSelectionDown = useCallback(
            () =>
                setIndexSelected((prevIndex) =>
                    prevIndex >= suggestions.size - 1
                        ? suggestions.size - 1
                        : prevIndex + 1,
                ),
            [suggestions.size],
        );

        useImperativeHandle(
            ref,
            () => ({
                moveSelectionUp,
                moveSelectionDown,
                confirmSelection,
            }),
            [moveSelectionUp, moveSelectionDown, confirmSelection],
        );
        // useImperativeHandle makes it so that the parent component can access
        // moveSelectionUp, moveSelectionDown, confirmSelection via the ref

        // react-window never clears its row height cache on its own. A heading
        // is shorter than a colleague row, so the cache goes stale when the
        // rows change.
        variableSizeListRef.current?.resetAfterIndex(0, false);

        return (
            <ClickAwayListener onClickAway={onCloseRequested}>
                {suggestions.size === 0 && text !== '@' ? null : (
                    <div
                        className="absolute z-40 w-[360px] -translate-y-full overflow-y-scroll rounded-sm bg-white p-2 shadow-lg outline-hidden"
                        style={listStyle}
                    >
                        <AutoSizer>
                            {({ height, width }) => (
                                <VariableSizeList
                                    ref={variableSizeListRef}
                                    height={height}
                                    itemCount={rows.length}
                                    itemSize={(index) =>
                                        getRowHeight(rows[index])
                                    }
                                    width={width}
                                    outerRef={listRef}
                                    overscanCount={4}
                                    itemData={rowData}
                                    itemKey={getRowKey}
                                >
                                    {MentionPickerRow}
                                </VariableSizeList>
                            )}
                        </AutoSizer>
                    </div>
                )}
            </ClickAwayListener>
        );
    },
);

MentionPicker.displayName = 'MentionPicker';
export default MentionPicker;

// createMentionDTO in InputActions needs a chat user and finds a mention with
// getContactName. Without both, a colleague can be picked but is never
// mentioned.
const extensionIsMentionable = (extension: TypedMap<Extension>) =>
    !!extension.get('chatUserKey') &&
    !!getContactName(extension.get('contact'));

const getMentioneeName = (mentionee: TypedMap<Extension>) =>
    extractName(mentionee.get('contact'));

const getMentioneeMatchesText = (
    mentionee: TypedMap<Extension>,
    text?: string,
) => {
    const name = extractName(mentionee?.get('contact'))?.toLowerCase();
    return (
        name?.startsWith(text) ||
        name?.split(' ').some((part: string) => part.startsWith(text))
    );
};

const possibleMentionRegExp = new RegExp(
    /(?<=^|\s)@\w*(?:\s\w+)?$|(?<=^|\s)@\w+\s$/,
);

export const getPossibleMentionString = (string?: string) => {
    const regExpResult = possibleMentionRegExp.exec(string);
    return regExpResult ? regExpResult[0] : '';
};
